import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  AutoAwesome,
  IntegrationInstructions,
  Speed,
  Analytics,
  Security,
  Devices,
  Code,
  CloudUpload,
  Timeline
} from '@mui/icons-material';

// Dark theme colors
const colors = {
  bgPrimary: '#0a0a0a',
  bgSecondary: '#141414',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  success: '#22c55e'
};

const FeaturesPage = () => {
  const navigate = useNavigate();

  const mainFeatures = [
    {
      id: 'ai-estimation',
      icon: <AutoAwesome />,
      title: 'AI-Assisted Estimation',
      description: 'Get AI-generated story point suggestions based on item descriptions. Use them as a starting point for discussion.',
      benefits: [
        'Suggestions based on item description',
        'Confidence indicators for each estimate',
        'References to similar past items',
        'Works with any deck type'
      ],
      demo: {
        title: 'User Authentication System',
        suggestion: '8 points',
        confidence: 'High confidence',
        reasoning: [
          { name: 'OAuth Integration', points: '8 pts' },
          { name: 'Login System', points: '5 pts' },
          { name: 'User Registration', points: '8 pts' }
        ]
      }
    },
    {
      id: 'jira-integration',
      icon: <IntegrationInstructions />,
      title: 'Jira Integration',
      description: 'Import issues from Jira and push estimates back when voting is complete.',
      benefits: [
        'Import issues with full context',
        'Push story points back to Jira',
        'View and post comments',
        'Support for JQL queries'
      ],
      demo: {
        flow: ['Import Issues', 'Estimate', 'Sync Back'],
        items: [
          { key: 'PROJ-123', title: 'API Rate Limiting', points: '5 pts' },
          { key: 'PROJ-124', title: 'Database Migration', points: '13 pts' }
        ]
      }
    },
    {
      id: 'collaboration',
      icon: <Speed />,
      title: 'Real-Time Collaboration',
      description: 'Vote together in real-time with live updates and instant results reveal.',
      benefits: [
        'Live voting with instant updates',
        'See who has voted',
        'Automatic reconnection',
        'Mobile-friendly interface'
      ],
      demo: {
        participants: [
          { name: 'Sarah Anderson', status: 'voted' },
          { name: 'Michael Johnson', status: 'voted' },
          { name: 'Emily Liu', status: 'voting' },
          { name: 'David Kim', status: 'pending' }
        ],
        progress: 75
      }
    },
    {
      id: 'analytics',
      icon: <Analytics />,
      title: 'Team Analytics',
      description: 'Track estimation patterns and participation across your sessions.',
      benefits: [
        'View voting history',
        'Track participation rates',
        'Analyze estimation patterns',
        'Export session data'
      ],
      demo: {
        metrics: [
          { label: 'Story Points', value: '156' },
          { label: 'Sessions', value: '24' },
          { label: 'Items', value: '89' }
        ]
      }
    }
  ];

  const additionalFeatures = [
    {
      icon: <Devices />,
      title: 'Works on Any Device',
      description: 'Responsive design that works on desktop, tablet, and mobile browsers.'
    },
    {
      icon: <Security />,
      title: 'Role-Based Access',
      description: 'Control who can facilitate sessions, vote, and manage team settings.'
    },
    {
      icon: <Code />,
      title: 'Multiple Deck Types',
      description: 'Choose from Fibonacci, T-shirt sizes, powers of 2, or create custom decks.'
    },
    {
      icon: <CloudUpload />,
      title: 'Async Voting',
      description: 'Team members can vote on their own schedule with configurable deadlines.'
    },
    {
      icon: <Timeline />,
      title: 'Session History',
      description: 'Review past sessions and see how items were estimated.'
    }
  ];

  const FeatureDemo = ({ feature }) => {
    if (feature.id === 'ai-estimation') {
      return (
        <Paper elevation={0} sx={{ p: 3, bgcolor: colors.bgSurface, border: `1px solid ${colors.border}` }}>
          <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary }}>
            {feature.demo.title}
          </Typography>

          <Box
            sx={{
              bgcolor: colors.bgSecondary,
              p: 2,
              borderRadius: 1,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: `1px solid ${colors.border}`
            }}
          >
            <Chip icon={<AutoAwesome />} label="AI Suggestion" size="small" sx={{ bgcolor: colors.accentPrimary, color: '#fff' }} />
            <Typography variant="h4" sx={{ color: colors.accentPrimary }} fontWeight="bold">
              {feature.demo.suggestion}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              {feature.demo.confidence}
            </Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: colors.textSecondary }}>
            Similar past items:
          </Typography>

          {feature.demo.reasoning.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 1,
                bgcolor: colors.bgSecondary,
                borderRadius: 1,
                mb: 1,
                border: `1px solid ${colors.border}`
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textPrimary }}>{item.name}</Typography>
              <Typography variant="body2" sx={{ color: colors.accentPrimary }} fontWeight="bold">
                {item.points}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }

    if (feature.id === 'jira-integration') {
      return (
        <Paper elevation={0} sx={{ p: 3, bgcolor: colors.bgSurface, border: `1px solid ${colors.border}` }}>
          <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary }}>
            Jira Integration
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
            {feature.demo.flow.map((step, index) => (
              <React.Fragment key={index}>
                <Chip
                  label={step}
                  sx={{ fontWeight: 'bold', bgcolor: colors.accentPrimary, color: '#fff' }}
                />
                {index < feature.demo.flow.length - 1 && (
                  <Typography variant="h6" sx={{ color: colors.textMuted }}>→</Typography>
                )}
              </React.Fragment>
            ))}
          </Box>

          {feature.demo.items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: colors.bgSecondary,
                borderRadius: 1,
                mb: 1,
                border: `1px solid ${colors.border}`
              }}
            >
              <Chip
                label={item.key}
                size="small"
                sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold', bgcolor: colors.accentSecondary, color: '#fff' }}
              />
              <Typography variant="body2" sx={{ flexGrow: 1, color: colors.textPrimary }}>
                {item.title}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.accentPrimary }} fontWeight="bold">
                {item.points}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }

    if (feature.id === 'collaboration') {
      return (
        <Paper elevation={0} sx={{ p: 3, bgcolor: colors.bgSurface, border: `1px solid ${colors.border}` }}>
          <Typography variant="h6" gutterBottom sx={{ color: colors.textPrimary }}>
            Participants (4/5)
          </Typography>

          {feature.demo.participants.map((participant, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                mb: 1
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: colors.accentPrimary,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              >
                {participant.name.split(' ').map(n => n[0]).join('')}
              </Box>
              <Typography variant="body2" sx={{ flexGrow: 1, color: colors.textPrimary }}>
                {participant.name}
              </Typography>
              <Chip
                label={participant.status}
                size="small"
                sx={{
                  bgcolor: participant.status === 'voted' ? colors.success :
                           participant.status === 'voting' ? '#eab308' : colors.bgSecondary,
                  color: participant.status === 'pending' ? colors.textSecondary : '#fff'
                }}
              />
            </Box>
          ))}

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                width: '100%',
                height: 6,
                bgcolor: colors.bgSecondary,
                borderRadius: 3,
                overflow: 'hidden',
                mb: 1
              }}
            >
              <Box
                sx={{
                  width: `${feature.demo.progress}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${colors.accentPrimary}, ${colors.accentSecondary})`
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: colors.textSecondary }} display="block" textAlign="center">
              3 of 4 votes cast
            </Typography>
          </Box>
        </Paper>
      );
    }

    if (feature.id === 'analytics') {
      return (
        <Paper elevation={0} sx={{ p: 3, bgcolor: colors.bgSurface, border: `1px solid ${colors.border}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: colors.textPrimary }}>Team Analytics</Typography>
            <Chip label="Last 30 days" size="small" sx={{ bgcolor: colors.bgSecondary, color: colors.textSecondary }} />
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {feature.demo.metrics.map((metric, index) => (
              <Grid item xs={4} key={index}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" sx={{ color: colors.textPrimary }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {metric.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" gutterBottom sx={{ color: colors.textSecondary }}>
            Recent Sessions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 60 }}>
            {[60, 75, 85, 70, 90].map((height, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: `${height}%`,
                  background: `linear-gradient(180deg, ${colors.accentPrimary}, ${colors.accentSecondary})`,
                  borderRadius: '2px 2px 0 0',
                  minHeight: 10
                }}
              />
            ))}
          </Box>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 100%)`,
          py: { xs: 8, md: 10 },
          textAlign: 'center',
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 3,
              background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Features for Modern Planning
          </Typography>

          <Typography variant="h5" sx={{ mb: 4, color: colors.textSecondary }}>
            Everything you need for collaborative estimation.
            Real-time voting, async mode, AI suggestions, and Jira integration.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.accentPrimary} 20%, ${colors.accentSecondary} 100%)`
                }
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: colors.border,
                color: colors.textPrimary,
                '&:hover': {
                  borderColor: colors.textSecondary,
                  bgcolor: 'rgba(255,255,255,0.05)'
                }
              }}
            >
              View Pricing
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        {mainFeatures.map((feature, index) => (
          <Box key={feature.id} sx={{ mb: { xs: 8, md: 12 } }}>
            <Grid
              container
              spacing={4}
              alignItems="center"
              direction={index % 2 === 0 ? 'row' : 'row-reverse'}
            >
              <Grid item xs={12} md={6}>
                <Chip
                  icon={feature.icon}
                  label={feature.id === 'ai-estimation' ? 'AI-Assisted' :
                        feature.id === 'jira-integration' ? 'Integration' :
                        feature.id === 'collaboration' ? 'Real-time' : 'Analytics'}
                  sx={{ mb: 2, bgcolor: colors.bgSurface, color: colors.accentPrimary, border: `1px solid ${colors.border}` }}
                />

                <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
                  {feature.title}
                </Typography>

                <Typography variant="h6" sx={{ mb: 3, lineHeight: 1.7, color: colors.textSecondary }}>
                  {feature.description}
                </Typography>

                <List>
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <ListItem key={benefitIndex} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: colors.success }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} sx={{ '& .MuiListItemText-primary': { color: colors.textPrimary } }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <FeatureDemo feature={feature} />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Container>

      {/* Additional Features */}
      <Box sx={{ bgcolor: colors.bgSecondary, py: { xs: 8, md: 12 }, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h2" component="h2" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
              More Features
            </Typography>
            <Typography variant="h5" sx={{ color: colors.textSecondary }}>
              Everything you need for collaborative estimation
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {additionalFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: colors.bgSurface,
                    border: `1px solid ${colors.border}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: colors.accentPrimary
                    }
                  }}
                  elevation={0}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: colors.accentPrimary,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                      }}
                    >
                      {feature.icon}
                    </Box>

                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold" sx={{ color: colors.textPrimary }}>
                      {feature.title}
                    </Typography>

                    <Typography variant="body1" sx={{ color: colors.textSecondary }} lineHeight={1.6}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" gutterBottom fontWeight="bold">
            Start Planning Today
          </Typography>

          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            Free plan available. No credit card required.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                px: 4,
                py: 1.5,
                bgcolor: 'white',
                color: colors.accentPrimary,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              View Pricing
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturesPage;
